import { createSignal, For, Show, createMemo, createEffect, Switch, Match } from "solid-js";
import { useUserContext } from "./UserContext";
import socket from "./socket";
import { useI18n } from "./i18n";

// Utility: group fields into sections when a field of type 'group' is encountered.
// Returns an array where each element is either a field object or a group object
// with a `children` array.
// A field with type 'group-end' will close the current group.
export function groupFields(fields = []) {
  const out = [];
  let currentGroup = null;
  for (const f of fields || []) {
    if (f && f.type === "group") {
      // start a new group
      currentGroup = { ...f, children: [] };
      out.push(currentGroup);
    } else if (f && f.type === "group-end") {
      // end current group
      currentGroup = null;
      // Note: group-end itself is not rendered, just closes the group
    } else {
      if (currentGroup) {
        currentGroup.children.push(f);
      } else {
        out.push(f);
      }
    }
  }
  return out;
}


// Render convention details with action buttons
export const ConventionDetails = (props: {
  convention: any, 
  startEditing: (convention: any) => void | null,
  conventionStructure: any
}) => {

  const t = useI18n();
  createEffect(() => {
    console.log('Rendering ConventionDetails for convention', props.convention, 'with structure', JSON.parse(JSON.stringify(props.conventionStructure)));
  });

  return (
    <>
      <h5 class="card-title">
        {props.convention.info?.title || `${t("Internship Agreement")} (${props.convention.year})`}
      </h5>

      <ConventionFieldsPreview fields={props.conventionStructure.fields} values={props.convention.info} />

      {/* Only show "Previous Feedback" alert on active conventions with feedback history */}
      <Show when={props.convention.status === 'active' && props.convention.info?.feedbackHistory && props.convention.info.feedbackHistory.length > 0}>
        <div class="alert alert-info mt-3">
          <strong>{t("Previous Feedback")}:</strong> {t("You have received feedback on earlier versions of this convention.")}
        </div>
      </Show>

      {/* Action buttons for active conventions */}
      <Show when={props.convention.status === 'active' && props.startEditing}>
        <button type="button" onClick={() => props.startEditing(props.convention)} class="btn btn-primary me-2">
          {t("Edit")}
        </button>

        <button type="button" onClick={() => actions.deleteConvention(props.convention.id)} class="btn btn-danger">
          {t("Delete")}
        </button>
      </Show>
      
      {/* Allow editing non-validation fields for pending/validated conventions */}
      <Show when={(props.convention.status === 'pending' || props.convention.status === 'validated') && props.startEditing}>
        <button type="button" onClick={() => props.startEditing(props.convention)} class="btn btn-outline-primary me-2">
          <i class="fas fa-edit me-1"></i>
          {t("Edit non-critical fields")}
        </button>
      </Show>
    </>
  );
};


// Read-only preview component for a convention's fields. Reusable in admin preview.
export function ConventionFieldsPreview(props) {
  const grouped = createMemo(() => {
    const fields = props.fields || [];
    return groupFields(fields);
  });

  const formatFieldValue = (field, value) => {
    if (!value) return null;
    
    if (field.type === "checkbox") {
      return value === true || value === "true" ? "✓ Yes" : "✗ No";
    }
    
    if (field.type === "select-multiple") {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return value;
    }
    
    return value;
  };

  return (
    <div>
      <For each={grouped()}>{(item) => (
        <Show
          when={item && item.children}
          fallback={
            <div class="mb-3 row">
              <div class="col-md-4">
                <strong class="text-secondary">
                  {item.label}
                  <Show when={item.required}>
                    <span class="text-danger">*</span>
                  </Show>
                </strong>
              </div>
              <div class="col-md-8">
                <Show
                  when={props.values?.[item.name]}
                  fallback={<span class="text-muted fst-italic">{tPlaceholder(item)}</span>}
                >
                  <div class="text-dark" style="white-space: pre-wrap">
                    {formatFieldValue(item, props.values[item.name])}
                  </div>
                </Show>
              </div>
            </div>
          }
        >
          <div class="border rounded p-3 mb-3 bg-light">
            <h5 class="border-bottom pb-2 mb-3 text-primary">{item.label}</h5>
            <div class="ps-3">
              <For each={item.children}>{(f) => (
                <div class="mb-3 row">
                  <div class="col-md-4">
                    <strong class="text-secondary">
                      {f.label}
                      <Show when={f.required}>
                        <span class="text-danger">*</span>
                      </Show>
                    </strong>
                  </div>
                  <div class="col-md-8">
                    <Show
                      when={props.values?.[f.name]}
                      fallback={<span class="text-muted fst-italic">{tPlaceholder(f)}</span>}
                    >
                      <div class="text-dark" style="white-space: pre-wrap">
                        {formatFieldValue(f, props.values[f.name])}
                      </div>
                    </Show>
                  </div>
                </div>
              )}</For>
            </div>
          </div>
        </Show>
      )}</For>
    </div>
  );
}

// Helper to return a placeholder text for preview
function tPlaceholder(f) {
  if (!f) return "";
  if (f.type === "textarea") return "---";
  if (f.type === "date") return "YYYY-MM-DD";
  if (f.type === "checkbox") return "☐ Not checked";
  if (f.type === "select-single") return "No option selected";
  if (f.type === "select-multiple") return "No options selected";
  return "";
}

// Interactive form component for students/teachers to fill convention fields.
export function ConventionFieldsForm(props) {
  
  const grouped = createMemo(() => {
    const fields = props.fields || [];
    return groupFields(fields);
  });

  return (
    <div>
      <For each={grouped()}>{(item) => (
        <Show
          when={item && item.children}
          fallback={
            <FieldInput 
              f={item} 
              values={props.values} 
              onChange={props.onChange} 
              readonly={props.readonly}
              editableFields={props.editableFields}
            />
          }
        >
          <div class="border rounded p-3 mb-3 bg-light">
            <h5 class="border-bottom pb-2 mb-3">{item.label}</h5>
            <div class="ps-3">
              <For each={item.children}>{(f) => (
                <FieldInput 
                  f={f} 
                  values={props.values} 
                  onChange={props.onChange} 
                  readonly={props.readonly}
                  editableFields={props.editableFields}
                />
              )}</For>
            </div>
          </div>
        </Show>
      )}</For>
    </div>
  );
}

function FieldInput(props) {
  const t = useI18n();
  
  // Determine if this specific field should be readonly
  const isFieldReadonly = createMemo(() => {
    if (!props.readonly) return false;
    // If editableFields is provided and this field is in it, it's editable
    if (props.editableFields && props.editableFields.includes(props.f.name)) {
      return false;
    }
    return true;
  });
  
  // Check if field requires validation
  const requiresValidation = createMemo(() => props.f.requiresValidation !== false);

  return (
    <Switch fallback={
      <div class="form-floating mb-3">
        <input
          type="text"
          class="form-control"
          id={props.f.name}
          placeholder={props.f.label}
          value={props.values[props.f.name] || ""}
          onInput={(e) => props.onChange(props.f.name, e.target.value)}
          required={props.f.required}
          disabled={isFieldReadonly()}
        />
        <label for={props.f.name}>
          {props.f.label}
          <Show when={props.f.required}>
            <span class="text-danger ms-1">*</span>
          </Show>
          <Show when={requiresValidation()}>
            <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
              <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
            </span>
          </Show>
        </label>
      </div>
    }>
      <Match when={props.f.type === "checkbox"}>
        <div class="form-check mb-3">
          <input
            class="form-check-input"
            type="checkbox"
            id={props.f.name}
            checked={props.values[props.f.name] === true || props.values[props.f.name] === "true"}
            onChange={(e) => props.onChange(props.f.name, e.target.checked)}
            disabled={isFieldReadonly()}
          />
          <label class="form-check-label" for={props.f.name}>
            {props.f.label}
            <Show when={props.f.required}>
              <span class="text-danger ms-1">*</span>
            </Show>
            <Show when={requiresValidation()}>
              <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
                <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
              </span>
            </Show>
          </label>
        </div>
      </Match>

      <Match when={props.f.type === "textarea"}>
        <div class="form-floating mb-3">
          <textarea
            class="form-control"
            id={props.f.name}
            placeholder={props.f.label}
            style="height: 100px"
            value={props.values[props.f.name] || ""}
            onInput={(e) => props.onChange(props.f.name, e.target.value)}
            required={props.f.required}
            disabled={isFieldReadonly()}
          ></textarea>
          <label for={props.f.name}>
            {props.f.label}
            <Show when={props.f.required}>
              <span class="text-danger ms-1">*</span>
            </Show>
            <Show when={requiresValidation()}>
              <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
                <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
              </span>
            </Show>
          </label>
        </div>
      </Match>

      <Match when={props.f.type === "date"}>
        <div class="form-floating mb-3">
          <input
            type="date"
            class="form-control"
            id={props.f.name}
            placeholder={props.f.label}
            value={props.values[props.f.name] || ""}
            onInput={(e) => props.onChange(props.f.name, e.target.value)}
            required={props.f.required}
            disabled={isFieldReadonly()}
          />
          <label for={props.f.name}>
            {props.f.label}
            <Show when={props.f.required}>
              <span class="text-danger ms-1">*</span>
            </Show>
            <Show when={requiresValidation()}>
              <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
                <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
              </span>
            </Show>
          </label>
        </div>
      </Match>

      <Match when={props.f.type === "select-single"}>
        <div class="form-floating mb-3">
          <select
            class="form-select"
            id={props.f.name}
            value={props.values[props.f.name] || ""}
            onChange={(e) => props.onChange(props.f.name, e.target.value)}
            required={props.f.required}
            disabled={isFieldReadonly()}
          >
            <option value="">{props.f.label}</option>
            <For each={props.f.options || []}>
              {(option) => <option value={option}>{option}</option>}
            </For>
          </select>
          <label for={props.f.name}>
            {props.f.label}
            <Show when={props.f.required}>
              <span class="text-danger ms-1">*</span>
            </Show>
            <Show when={requiresValidation()}>
              <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
                <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
              </span>
            </Show>
          </label>
        </div>
      </Match>

      <Match when={props.f.type === "select-multiple"}>
        <div class="mb-3">
          <label class="form-label">
            {props.f.label}
            <Show when={props.f.required}>
              <span class="text-danger ms-1">*</span>
            </Show>
            <Show when={requiresValidation()}>
              <span class="badge bg-warning text-dark ms-2" style="font-size: 0.65rem;" title={t("This field requires teacher validation")}>
                <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
              </span>
            </Show>
          </label>
          <div class="border rounded p-2">
            <For each={props.f.options || []}>
              {(option) => {
                const isChecked = createMemo(() => {
                  const value = props.values[props.f.name];
                  if (Array.isArray(value)) {
                    return value.includes(option);
                  }
                  return false;
                });

                const handleChange = (checked: boolean) => {
                  const currentValue = Array.isArray(props.values[props.f.name]) 
                    ? props.values[props.f.name] 
                    : [];
                  
                  let newValue;
                  if (checked) {
                    newValue = [...currentValue, option];
                  } else {
                    newValue = currentValue.filter(v => v !== option);
                  }
                  props.onChange(props.f.name, newValue);
                };

                return (
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id={`${props.f.name}_${option}`}
                      checked={isChecked()}
                      onChange={(e) => handleChange(e.target.checked)}
                      disabled={isFieldReadonly()}
                    />
                    <label class="form-check-label" for={`${props.f.name}_${option}`}>
                      {option}
                    </label>
                  </div>
                );
              }}
            </For>
          </div>
        </div>
      </Match>
    </Switch>
  );
}

export default function Conventions() {
  const t = useI18n();
  const [userStore, setUserStore, actions] = useUserContext();
  const [editingId, setEditingId] = createSignal(null);
  const [editForm, setEditForm] = createSignal({});
  const [errorMessage, setErrorMessage] = createSignal(null);

  // Group conventions by status
  const groupedConventions = createMemo(() => {
    const active = [];
    const pending = [];
    const validated = [];
    const locked = [];
    const archived = [];
    userStore.conventions.forEach(convention => {
      if (convention.status === 'active') {
        active.push(convention);
      } else if (convention.status === 'pending') {
        pending.push(convention);
      } else if (convention.status === 'validated') {
        validated.push(convention);
      } else if (convention.status === 'locked') {
        locked.push(convention);
      } else if (convention.status === 'archived') {
        archived.push(convention);
      }
    });

    return { active, pending, validated, locked, archived };
  });

  // Add event listener for convention errors
  if (!userStore.conventionErrorHandlerSet) {
    socket.on('convention-error', (error) => {
      setErrorMessage(error.message);
      // Clear the error message after 5 seconds
      setTimeout(() => setErrorMessage(null), 5000);
    });

    setUserStore('conventionErrorHandlerSet', true);
  }

  // Helper function to initialize empty form (using createMemo to cache based on structure)
  const createEmptyForm = createMemo(() => {
    // Create an object with empty values for all fields in the structure
    const form = {};
    if (userStore.conventionStructure && userStore.conventionStructure.fields) {
      userStore.conventionStructure.fields.forEach(field => {
        if (field.type === "checkbox") {
          form[field.name] = false;
        } else if (field.type === "select-multiple") {
          form[field.name] = [];
        } else {
          form[field.name] = "";
        }
      });
    }
    return form;
  });

  const startEditing = (convention) => {
    setEditingId(convention.id);
    // Initialize form with values from convention or empty strings if not present
    const initialForm = { ...createEmptyForm() };
    // Fill in existing values
    if (convention.info) {
      Object.keys(convention.info).forEach(key => {
        if (initialForm[key] !== undefined) {
          initialForm[key] = convention.info[key];
        }
      });
    }
    setEditForm(initialForm);
  };

  const saveEditing = () => {
    actions.updateConvention(editingId(), editForm());
    setEditingId(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const changeStatus = (conventionId, newStatus) => {
    actions.changeConventionStatus(conventionId, newStatus);
  };

  const requestFeedback = (conventionId) => {
    if (confirm(t('Are you sure you want to request feedback? This will make your convention visible to teachers for validation.'))) {
      changeStatus(conventionId, 'pending');
    }
  };

  const makeActive = (conventionId) => {
    // Find the convention
    const convention = userStore.conventions.find(c => c.id === conventionId);
    if (convention) {
      // If convention was previously validated, explain that it will be restored as validated
      if (convention.info && convention.info.fullyValidated) {
        if (confirm(t('This convention was previously validated. It will be restored as a validated convention. Continue?'))) {
          changeStatus(conventionId, 'active');
        }
      }
      // If convention is currently pending, explain that it will no longer be visible to teachers
      else if (convention.status === 'pending') {
        if (confirm(t('Moving back to draft mode will hide this convention from teachers. You will need to request feedback again after making your changes. Continue?'))) {
          changeStatus(conventionId, 'active');
        }
      }
      else {
        changeStatus(conventionId, 'active');
      }
    }
  };

  const archiveConvention = (conventionId) => {
    //if (confirm('Are you sure you want to archive this convention?')) {
    changeStatus(conventionId, 'archived');
    //}
  };

  // Render a convention card with appropriate styling and actions based on status
  const renderConventionCard = (convention) => {
    const t = useI18n();
    
    // Define styling and actions based on status
    const cardConfig = {
      'active': {
        borderClass: 'border-primary',
        headerClass: 'bg-primary text-white',
        statusText: t('Active - Draft Mode'),
        actions: (
          <div>
            <button
              type="button"
              onClick={() => requestFeedback(convention.id)}
              class="btn btn-sm btn-light me-2"
              title={t("Submit for validation")}
              disabled={groupedConventions().locked.length > 0}
            >
              {t("Request Feedback")}
            </button>
            <button
              type="button"
              onClick={() => archiveConvention(convention.id)}
              class="btn btn-sm btn-outline-light"
              title={t("Archive this convention")}
              disabled={groupedConventions().locked.length > 0}
            >
              {t("Archive")}
            </button>
          </div>
        )
      },
      'pending': {
        borderClass: 'border-warning',
        headerClass: 'bg-warning',
        statusText: t('Awaiting Teacher Validation'),
        actions: (
          <div>
            <button
              type="button"
              onClick={() => makeActive(convention.id)}
              class="btn btn-sm btn-outline-primary me-2"
              title={t("Move back to draft mode for editing")}
              disabled={groupedConventions().locked.length > 0}
            >
              {t("Back to Draft")}
            </button>
            <button
              type="button"
              onClick={() => archiveConvention(convention.id)}
              class="btn btn-sm btn-outline-dark"
              title={t("Archive this convention")}
              disabled={groupedConventions().locked.length > 0}
            >
              {t("Archive")}
            </button>
          </div>
        ),
        extraInfo: (
          <div class="alert alert-info">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                {t("Feedback requested on")} {new Date(convention.info?.requestedFeedbackDate).toLocaleString()}
              </div>
              <div>
                <Show when={convention.info?.validations && convention.info.validations.length > 0}>
                  <span class="badge bg-primary">{convention.info.validations.length} {t("teachers validated")}</span>
                </Show>
              </div>
            </div>
          </div>
        )
      },
      'validated': {
        borderClass: 'border-success',
        headerClass: 'bg-success text-white',
        statusText: t('Validated by All Teachers'),
        actions: (
          <div>
            <button
              type="button"
              onClick={() => archiveConvention(convention.id)}
              class="btn btn-sm btn-outline-light"
              title={t("Archive this convention")}
              disabled={groupedConventions().locked.length > 0}
            >
              {t("Archive")}
            </button>
          </div>
        ),
        extraInfo: (
          <div class="alert alert-success">
            <strong>{t("Fully Validated!")}</strong> {t("All teachers have approved your internship agreement.")}
          </div>
        )
      }
    };

    const config = createMemo(() => cardConfig[convention.status] || {});
    
    const currentEditableFields = createMemo(() => 
      userStore.conventionStructure.fields
        .filter(field => field.requiresValidation === false)
        .map(field => field.name)
    );

    return (
      <div class={`card mb-3 ${config().borderClass || 'border'}`}>
        <div class={`card-header ${config().headerClass || ''} d-flex justify-content-between align-items-center`}>
          <span>{config().statusText || convention.status}</span>
          {config().actions}
        </div>
        <div class="card-body">
          <Show
            when={editingId() === convention.id}
            fallback={
              <>
                <ConventionDetails 
                  convention={convention} 
                  startEditing={startEditing} 
                  conventionStructure={userStore.conventionStructure}
                />
                {config().extraInfo}
                
                {/* Show editable fields info for pending/validated conventions */}
                <Show when={(convention.status === 'pending' || convention.status === 'validated')}>
                  <div class="alert alert-info mt-3">
                    <i class="fas fa-info-circle me-2"></i>
                    {t("You can still edit fields that don't require teacher validation. Click Edit to modify them.")}
                  </div>
                </Show>
                
                {/* Version notification for pending conventions */}
                <Show when={convention.status === 'pending' && convention.info?.feedbackHistory && 
                            convention.info.feedbackHistory.length > 0 && 
                            convention.info.feedbackHistory[0].conventionVersion === convention.info.version}>
                  <div class="alert alert-secondary mt-3">
                    {t("See below the feedback history, the current version is Version")} {convention.info.version}.
                  </div>
                </Show>
                
                {/* Feedback History */}
                <Show when={convention.info?.feedbackHistory && convention.info.feedbackHistory.length > 0}>
                  <div class="card mt-3 mb-3">
                    <div class="card-header bg-light">
                      <strong>{t("Feedback History")}</strong>
                    </div>
                    <div class="card-body p-0">
                      <div class="list-group list-group-flush">
                        <For each={convention.info.feedbackHistory}>
                          {(historyItem, index) => (
                            <div class="list-group-item">
                              <div class="d-flex justify-content-between align-items-center mb-1">
                                <strong>{t("Version")} {historyItem.conventionVersion || '?'}</strong>
                                <small class="text-muted">
                                  {t("by")} {historyItem.teacher} {t("on")} {new Date(historyItem.date).toLocaleString()}
                                </small>
                              </div>
                              <p class="mb-1" style="white-space: pre-wrap">{historyItem.feedback}</p>
                              <div class="d-flex gap-2">
                                <Show when={historyItem.notificationSent}>
                                  <small class="badge bg-info">
                                    <i class="fas fa-envelope me-1"></i> {t("Notification received")}
                                  </small>
                                </Show>
                                <Show when={historyItem.validations && historyItem.validations.length > 0}>
                                  <small class="text-muted">
                                    {t("Validated by")} {historyItem.validations.length} {t("teacher(s)")}
                                  </small>
                                </Show>
                              </div>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  </div>
                </Show>
                
                {/* Validators list for pending conventions */}
                <Show when={convention.status === 'pending' && convention.info?.validations && convention.info.validations.length > 0}>
                  <div class="mt-3">
                    <h6>{t("Validators")}:</h6>
                    <ul class="list-group">
                      <For each={convention.info.validations}>
                        {(validation) => (
                          <li class="list-group-item d-flex justify-content-between align-items-center">
                            <span>{validation.validatorName}</span>
                            <small class="text-muted">{new Date(validation.timestamp).toLocaleString()}</small>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>
              </>
            }
          >
          <form>
            <Show when={(convention.status === 'pending' || convention.status === 'validated')}>
              <div class="alert alert-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>{t("Limited editing mode")}:</strong> {t("Only fields that don't require teacher validation can be edited. Fields requiring validation are disabled.")}
              </div>
            </Show>
            
            {/* Field legend */}
            <div class="alert alert-light border mb-3">
              <small>
                <strong>{t("Field indicators")}:</strong>
                <span class="ms-3">
                  <span class="text-danger">*</span> = {t("Required field")}
                </span>
                <span class="ms-3">
                  <span class="badge bg-warning text-dark" style="font-size: 0.65rem;">
                    <i class="fas fa-user-check" style="font-size: 0.6rem;"></i>
                  </span> = {t("Requires teacher validation")}
                </span>
              </small>
            </div>
            
            <ConventionFieldsForm
              fields={userStore.conventionStructure.fields}
              values={editForm()}
              onChange={(name, value) => setEditForm({ ...editForm(), [name]: value })}
              readonly={convention.status === 'pending' || convention.status === 'validated'}
              editableFields={currentEditableFields()}
            />

            <button type="button" onClick={saveEditing} class="btn btn-success me-2">
              {t("Save")}
            </button>

            <button type="button" onClick={cancelEditing} class="btn btn-secondary">
              {t("Cancel")}
            </button>
          </form>
          </Show>
        </div>
      </div>
    );
  };

  return (
    <div class="container mt-4">
      <h2>{t("Your Internship Agreements")}</h2>

      {/* Show error message if any */}
      <Show when={errorMessage()}>
        <div class="alert alert-danger">{errorMessage()}</div>
      </Show>

      {/* Convention Status Guide */}
      <div class="card mb-4">
        <div class="card-header">
          {t("Convention Status Guide")}
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-3">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-primary me-2">{t("Draft")}</span>
                <span>{t("Private to you")}</span>
              </div>
              <small class="text-muted">{t("Edit and prepare your convention")}</small>
            </div>
            <div class="col-md-3">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-warning me-2">{t("Pending")}</span>
                <span>{t("Visible to teachers")}</span>
              </div>
              <small class="text-muted">{t("Awaiting teacher validation")}</small>
            </div>
            <div class="col-md-3">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-success me-2">{t("Validated")}</span>
                <span>{t("Fully approved")}</span>
              </div>
              <small class="text-muted">{t("All teachers have validated")}</small>
            </div>
            <div class="col-md-3">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-secondary me-2">{t("Archived")}</span>
                <span>{t("Inactive")}</span>
              </div>
              <small class="text-muted">{t("Stored for reference")}</small>
            </div>
          </div>
        </div>
      </div>

      {/* Create button - only shown if no active or pending conventions */}
      <Show
        when={
          groupedConventions().active.length === 0 &&
          groupedConventions().pending.length === 0 &&
          groupedConventions().validated.length === 0 &&
          groupedConventions().locked.length === 0
        }
        fallback={
          <Show
            when={
              groupedConventions().locked.length > 0
            }
            fallback={<div class="alert alert-info mb-4">
                {t("You already have an active convention. To create a new one, you need to archive your current active convention.")}
              </div>
            }
          ><div class="alert alert-info mb-4">
            {t("You already have a locked convention. You cannot create new conventions while having a locked one.")}
          </div></Show>
        }
      >
        <button
          type="button"
          onClick={() => actions.createConvention()}
          class="btn btn-primary mb-4"
        >
          {t("Create New Agreement")}
        </button>
      </Show>

      <Show
        when={userStore.conventions.length > 0}
        fallback={<p>{t("You don't have any internship agreements yet. Create one to get started.")}</p>}
      >
        {/* Locked Convention */}
        <Show when={groupedConventions().locked.length > 0}>
          <h3 class="border-bottom pb-2 mt-4 text-danger">
            <i class="fas fa-lock me-2"></i>
            {t("Locked Convention")}
          </h3>
          <For each={groupedConventions().locked}>
            {(convention) => (
              <div class={`card mb-3 border-danger`}>
                <div class={`card-header bg-danger text-white d-flex justify-content-between align-items-center`}>
                  <span>
                    <i class="fas fa-lock me-2"></i>
                    {t("Locked by Administrator")}
                  </span>
                </div>
                <div class="card-body">
                  <ConventionDetails 
                    convention={convention} 
                    startEditing={null} 
                    conventionStructure={userStore.conventionStructure}
                  />
                  <div class="alert alert-warning mt-3">
                    <i class="fas fa-info-circle me-2"></i>
                    <strong>{t("This convention is locked.")}</strong> {t("You cannot modify or archive it. Contact an administrator if you need changes.")}
                  </div>
                  
                  {/* Validators list for locked conventions */}
                  <Show when={convention.info?.validations && convention.info.validations.length > 0}>
                    <div class="mt-3">
                      <h6>{t("Validators")}:</h6>
                      <ul class="list-group">
                        <For each={convention.info.validations}>
                          {(validation) => (
                            <li class="list-group-item d-flex justify-content-between align-items-center">
                              <span>{validation.validatorName}</span>
                              <small class="text-muted">{new Date(validation.timestamp).toLocaleString()}</small>
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </Show>
        
        {/* Active Convention */}
        <Show when={groupedConventions().active.length > 0}>
          <h3 class="border-bottom pb-2 mt-4">{t("Active Convention")}</h3>
          <For each={groupedConventions().active}>
            {(convention) => renderConventionCard(convention)}
          </For>
        </Show>
        
        {/* Pending Convention */}
        <Show when={groupedConventions().pending.length > 0}>
          <h3 class="border-bottom pb-2 mt-4">{t("Pending Validation")}</h3>
          <For each={groupedConventions().pending}>
            {(convention) => renderConventionCard(convention)}
          </For>
        </Show>

        {/* Validated Convention */}
        <Show when={groupedConventions().validated.length > 0}>
          <h3 class="border-bottom pb-2 mt-4">{t("Validated Convention")}</h3>
          <For each={groupedConventions().validated}>
            {(convention) => renderConventionCard(convention)}
          </For>
        </Show>

        {/* Archived Conventions - Keep as table for compactness */}
        <Show when={groupedConventions().archived.length > 0}>
          <h3 class="border-bottom pb-2 mt-4">{t("Archived Conventions")}</h3>
          <div class="table-responsive mb-4">
            <table class="table table-hover">
              <thead class="table-light">
                <tr>
                  <th>{t("Title")}</th>
                  <th>{t("Business")}</th>
                  <th>{t("Period")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={groupedConventions().archived}>
                  {(convention) => (
                    <tr class={convention.info?.fullyValidated ? "table-success" : ""}>
                      <td>
                        {convention.info?.title || `${t("Internship Agreement")} (${convention.year})`}
                        <Show when={convention.info?.fullyValidated}>
                          <span class="badge bg-success ms-2" title={t("This convention was validated by all teachers")}>{t("Validated")}</span>
                        </Show>
                      </td>
                      <td>{convention.info?.business || t("Not specified")}</td>
                      <td>
                        {convention.info?.startDate || t("Not specified")} - {convention.info?.endDate || t("Not specified")}
                      </td>
                      <td>
                        <div class="btn-group">
                          <button
                            type="button"
                            onClick={() => makeActive(convention.id)}
                            class={convention.info?.fullyValidated ? "btn btn-sm btn-success" : "btn btn-sm btn-primary"}
                            title={convention.info?.fullyValidated ?
                              t("Restore this convention as validated") :
                              t("Make this convention active")}
                            disabled={groupedConventions().locked.length > 0}
                          >
                            <i class="bi bi-arrow-up-circle"></i> {convention.info?.fullyValidated ? t("Restore") : t("Activate")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
    </div>
  );
}
